import {getClientVersion} from '@github-ui/client-version'
import {isFeatureEnabled} from '@github-ui/feature-flags'

import {versionMismatchRateLimiter} from './rate-limiter'

export interface MismatchDetails {
  clientVersion: string
  serverVersion?: string
  requestUrl: string
  responseHeaders: Headers
  appName?: string
}

export type VersionMismatchHandler = (details: MismatchDetails) => void

export interface VersionMismatchConfig {
  onMismatchDetected: VersionMismatchHandler
}

class VersionMismatchDetector {
  #appConfigs = new Map<string, VersionMismatchConfig>()
  // A route can fetch before the app that shows the notification registers (apps register in an
  // effect). Keep the first unhandled mismatch so that app still gets it once it registers.
  #pendingMismatch: MismatchDetails | null = null

  registerApp(appName: string, config: VersionMismatchConfig): void {
    this.#appConfigs.set(appName, config)

    const pending = this.#pendingMismatch
    if (pending && (!pending.appName || pending.appName === appName)) {
      this.#deliver(pending, [[appName, config]])
    }
  }

  unregisterApp(appName: string): void {
    this.#appConfigs.delete(appName)
  }

  checkResponse(response: Response, appName?: string): boolean {
    const versionMismatch = response.headers.get('X-GitHub-Version-Mismatch')
    if (versionMismatch !== 'true') {
      return false
    }

    if (!isFeatureEnabled('react_version_mismatch_detection')) {
      return false
    }

    if (!versionMismatchRateLimiter.canTrigger()) {
      return false
    }

    const clientVersion = getClientVersion()
    const details: MismatchDetails = {
      clientVersion,
      serverVersion: response.headers.get('X-GitHub-Server-Version') || undefined,
      requestUrl: response.url,
      responseHeaders: response.headers,
      appName,
    }

    const targets = this.#targetsFor(appName)
    if (targets.length === 0) {
      // Only a delivered notification counts against the rate limit.
      this.#pendingMismatch ??= details
      return false
    }

    return this.#deliver(details, targets)
  }

  #targetsFor(appName?: string): Array<[string, VersionMismatchConfig]> {
    if (!appName) {
      return [...this.#appConfigs.entries()]
    }
    const config = this.#appConfigs.get(appName)
    return config ? [[appName, config]] : []
  }

  #deliver(details: MismatchDetails, targets: Array<[string, VersionMismatchConfig]>): boolean {
    this.#pendingMismatch = null
    // Another tab may have shown the notification while this mismatch was pending.
    if (!versionMismatchRateLimiter.canTrigger()) {
      return false
    }

    versionMismatchRateLimiter.recordTrigger()
    for (const [registeredAppName, config] of targets) {
      config.onMismatchDetected({...details, appName: registeredAppName})
    }
    return true
  }
}

export const versionMismatchDetector = new VersionMismatchDetector()
